import { useNavigate } from "@tanstack/react-router";
// Components
import { DeleteDraftRequestDialog } from "./DeleteDraftRequestDialog";
import { DataTable } from "../../components/DataTable";
// MUI
import Box from "@mui/material/Box";
import Tooltip from "@mui/material/Tooltip";

const columns = [
  { accessorKey: "no", header: "No.", meta: { minWidth: 100 } },
  {
    accessorKey: "createdBy",
    header: "Requested By",
    meta: { minWidth: 100 },
  },
  { accessorKey: "orderDate", header: "Date", meta: { minWidth: 100 } },
  {
    accessorKey: "branches",
    header: "Staff Branches",
    meta: { minWidth: 200 },
    cell: (info) => {
      const branches = info.getValue();
      if (branches.length === 1) return branches[0].branchName;
      if (branches.length > 1) {
        // If multiple branches, show "Multiple Branches" and list them on hover
        return (
          <Tooltip title={branches.map((b) => b.branchName).join(", ")}>
            <span>Multiple Branches</span>
          </Tooltip>
        );
      }
      return null;
    },
  },
  {
    accessorKey: "status",
    header: "Status",
    meta: { minWidth: 300 },
    cell: (info) => {
      if (info.getValue() === "") return null;
      if (info.row.original.awaitingApproval === "Yes")
        return "Awaiting Approval";
      if (info.row.original.processing === "true") return "Processing..";
      return null;
    },
  },
  {
    id: "delete",
    header: "Delete",
    meta: { align: "right" },
    cell: (info) => (
      // The dialog sits inside a clickable row, so swallow the click here.
      <Box onClick={(event) => event.stopPropagation()}>
        <DeleteDraftRequestDialog
          orderNo={info.row.original.no}
          disabled={
            info.row.original.processing !== "false" &&
            info.row.original.awaitingApproval !== "No"
          }
        />
      </Box>
    ),
  },
];

export function RequestTable({
  data,
  loading,
  requestFilter,
  setRequestFilter,
  status,
  columnVisibilityModel,
  canMerge,
  selectedRows,
  setSelectedRows,
}) {
  const navigate = useNavigate();
  const requests = data?.order || [];
  const rowCount = Number(data?.paging?.noOfRecords) || 0;

  const pagination = {
    page: (requestFilter?.page ?? 1) - 1,
    pageSize: requestFilter?.pageSize ?? 25,
  };

  const handlePaginationChange = (next) => {
    setRequestFilter((prevState) => ({
      ...prevState,
      page: next.page + 1,
    }));
  };

  const handleRowClick = (row) => {
    if (row.processing === "false" && status !== "submitted") {
      const baseURL = status === "approvals" ? "draft" : "request";
      navigate({ to: `/${baseURL}/${row.no}` });
    }
  };

  // Only allow selection of rows that are not processing
  const isRowSelectable = (row) => row.processing !== "true";

  return (
    <DataTable
      aria-label="Requests"
      columns={columns}
      rows={requests}
      getRowId={(row) => row.no}
      columnVisibility={columnVisibilityModel}
      rowCount={rowCount}
      page={pagination.page}
      pageSize={pagination.pageSize}
      loading={loading}
      onPaginationChange={handlePaginationChange}
      onRowClick={handleRowClick}
      selectable={canMerge}
      selectedIds={selectedRows}
      onSelectionChange={setSelectedRows}
      isRowSelectable={isRowSelectable}
    />
  );
}
